/**
 * The frame every menu screen shares: a header with the screen's name between two colour-cycling
 * strips and a BACK button for mouse and touch, a hint line at the bottom, panels, buttons, and
 * the opening transition (the picture opens from the middle like a tube warming up).
 */

import { textWidth } from '../../render/font';
import { BLACK, CYAN, WHITE } from '../../render/palette';
import type { Screen } from '../../render/screen';
import type { Rect } from './menuModel';
import { inRect } from './menuModel';
import { drawRasterStrip } from './raster';
import { drawPointer } from './spinner';
import { ease, FAINT, H, INK, MUTED, RAMPS, SELECTED, W } from './theme';

/** The header's BACK button. */
export const BACK_RECT: Rect = { x: 0, y: 0, w: 60, h: 20 };
export const HEADER_Y = 6;
/** First free line below the header. */
export const CONTENT_TOP = 22;
export const FOOTER_Y = 191;

export function drawHeader(
  screen: Screen, title: string, ms: number, back: boolean, backHot: boolean, colour = CYAN,
): void {
  const cx = Math.floor(screen.width / 2);
  const tw = textWidth(title);
  const left0 = back ? BACK_RECT.x + BACK_RECT.w : 8;
  drawRasterStrip(screen, left0, cx - Math.ceil(tw / 2) - 10, HEADER_Y + 2, 3, ms, RAMPS.blue, -1);
  drawRasterStrip(screen, cx + Math.ceil(tw / 2) + 10, screen.width - 8, HEADER_Y + 2, 3, ms, RAMPS.blue, 1);
  screen.shadowText(title, cx, HEADER_Y, colour, BLACK, 'center');
  if (back) {
    const c = backHot ? SELECTED : MUTED;
    screen.text('◄', 6 - (backHot ? 1 : 0), HEADER_Y, c);
    screen.text('BACK', 16, HEADER_Y, c);
  }
}

export function backHit(x: number, y: number): boolean {
  return inRect(BACK_RECT, x, y);
}

/** The hint line at the bottom. */
export function drawFooter(screen: Screen, hint: string, colour = FAINT): void {
  screen.text(hint, Math.floor(screen.width / 2), FOOTER_Y, colour, 'center');
}

/** Names for the confirm and back buttons, for the input in use. */
export function keyWords(pad: boolean): { ok: string; back: string } {
  return pad ? { ok: 'A', back: 'B' } : { ok: 'ENTER', back: 'ESC' };
}

const REVEAL_MS = 260;

/** The opening transition, drawn over a screen for its first quarter second. */
export function drawReveal(screen: Screen, ms: number): void {
  if (ms >= REVEAL_MS) return;
  const open = ease(ms, REVEAL_MS);
  const half = Math.round((1 - open) * (screen.height / 2));
  if (half <= 0) return;
  screen.fill(0, 0, screen.width, half, BLACK);
  screen.fill(0, screen.height - half, screen.width, half, BLACK);
  screen.fill(0, half, screen.width, 1, WHITE);
  screen.fill(0, screen.height - half - 1, screen.width, 1, WHITE);
}

/** A panel: black inside, a dark frame with bright corners. */
export function drawPanel(screen: Screen, r: Rect, border = FAINT, corner = MUTED): void {
  screen.fill(r.x, r.y, r.w, r.h, BLACK);
  screen.box(r.x, r.y, r.w, r.h, border);
  const right = r.x + r.w - 2;
  const bottom = r.y + r.h - 2;
  for (const [cx, cy] of [[r.x, r.y], [right, r.y], [r.x, bottom], [right, bottom]] as const) {
    screen.fill(cx, cy, 2, 2, corner);
  }
}

export type ButtonState = 'idle' | 'selected' | 'disabled';

/** A button: its label centred, a spinning diamond and yellow frame when selected. */
export function drawButton(screen: Screen, r: Rect, label: string, state: ButtonState, ms: number): void {
  const on = state === 'selected';
  screen.fill(r.x, r.y, r.w, r.h, BLACK);
  screen.box(r.x, r.y, r.w, r.h, on ? SELECTED : FAINT);
  if (on) screen.box(r.x + 1, r.y + 1, r.w - 2, r.h - 2, FAINT);
  const ty = r.y + Math.floor((r.h - 8) / 2) + 1;
  const colour = state === 'disabled' ? FAINT : on ? SELECTED : INK;
  screen.text(label, r.x + Math.floor(r.w / 2), ty, colour, 'center');
  if (on && r.w >= textWidth(label) + 24) drawPointer(screen, r.x + 5, ty, ms);
}

/** Centre x of the menu screen. */
export const CX = W / 2;
export const BOTTOM = H;
